/**
 * AI Assistant Message
 *
 * Renders a single message in the conversation with markdown support.
 * Supports special code blocks for charts (piechart).
 */

import React, { useCallback } from 'react';
import { Box, Typography, Paper, alpha, useTheme, IconButton, Tooltip } from '@mui/material';
import { DownloadIcon } from "../../icons";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AiAssistantMessage as MessageType } from './AiAssistantStore';
import { InlinePieChart } from './InlinePieChart';
import { InlineBarChart } from './InlineBarChart';
import { InlineHorizontalBarChart } from './InlineHorizontalBarChart';

/**
 * Extracts table data and downloads as CSV
 */
function exportTableToCsv(tableElement: HTMLTableElement, filename = 'datos.csv') {
  const rows = tableElement.querySelectorAll('tr');
  const csvRows: string[] = [];

  rows.forEach((row) => {
    const cells = row.querySelectorAll('th, td');
    const rowData = Array.from(cells).map((cell) => {
      // Escape quotes and wrap in quotes if contains comma or newline
      let text = cell.textContent || '';
      text = text.replace(/"/g, '""');
      if (text.includes(',') || text.includes('\n') || text.includes('"')) {
        text = `"${text}"`;
      }
      return text;
    });
    csvRows.push(rowData.join(','));
  });

  const csvContent = csvRows.join('\n');
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Wrapper component for tables with export button
 */
const ExportableTable: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const tableRef = React.useRef<HTMLTableElement>(null);

  const handleExport = useCallback(() => {
    if (tableRef.current) {
      const timestamp = new Date().toISOString().slice(0, 10);
      exportTableToCsv(tableRef.current, `datos-${timestamp}.csv`);
    }
  }, []);

  return (
    <Box sx={{ position: 'relative', my: 1 }}>
      <Tooltip title="Exportar CSV" arrow>
        <IconButton
          size="small"
          onClick={handleExport}
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            border: `1px solid ${theme.palette.divider}`,
            padding: '2px',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            zIndex: 1,
          }}
        >
          <DownloadIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <table ref={tableRef}>{children}</table>
    </Box>
  );
};

interface Props {
  message: MessageType;
  isStreaming?: boolean;
}

export const AiAssistantMessage: React.FC<Props> = ({ message, isStreaming }) => {
  const theme = useTheme();
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: isUser ? '85%' : '95%',
          px: 2,
          py: 1.5,
          borderRadius: 2,
          backgroundColor: isUser
            ? alpha(theme.palette.primary.main, 0.1)
            : alpha(theme.palette.grey[100], theme.palette.mode === 'dark' ? 0.1 : 1),
          border: `1px solid ${
            isUser
              ? alpha(theme.palette.primary.main, 0.2)
              : theme.palette.divider
          }`,
        }}
      >
        {isUser ? (
          <Typography variant="body2">{message.content}</Typography>
        ) : (
          <Box
            sx={{
              '& p': {
                m: 0,
                mb: 1,
                '&:last-child': { mb: 0 },
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                my: 1,
                fontSize: '0.875rem',
              },
              '& th, & td': {
                border: `1px solid ${theme.palette.divider}`,
                px: 1,
                py: 0.5,
                textAlign: 'left',
              },
              '& th': {
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                fontWeight: 600,
              },
              '& tr:nth-of-type(even)': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
              '& code': {
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: '0.85em',
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: alpha(theme.palette.grey[900], 0.05),
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                '& code': {
                  backgroundColor: 'transparent',
                  p: 0,
                },
              },
              '& ul, & ol': {
                pl: 2,
                my: 1,
              },
              '& li': {
                mb: 0.5,
              },
              '& strong': {
                fontWeight: 600,
              },
              '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 2,
                mb: 1,
                fontWeight: 600,
              },
              '& h1': { fontSize: '1.25rem' },
              '& h2': { fontSize: '1.1rem' },
              '& h3': { fontSize: '1rem' },
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Wrap tables with export button
                table({ children }) {
                  return <ExportableTable>{children}</ExportableTable>;
                },
                code({ className, children, ...props }) {
                  const match = /language-(\w+)/.exec(className || '');
                  const lang = match?.[1];

                  // Handle chart blocks
                  if (lang === 'piechart' || lang === 'barchart' || lang === 'horizontalbarchart') {
                    try {
                      const chartData = JSON.parse(String(children).trim());
                      if (chartData.title && Array.isArray(chartData.data)) {
                        if (lang === 'piechart') {
                          return <InlinePieChart title={chartData.title} data={chartData.data} />;
                        }
                        if (lang === 'barchart') {
                          return <InlineBarChart title={chartData.title} data={chartData.data} />;
                        }
                        if (lang === 'horizontalbarchart') {
                          return <InlineHorizontalBarChart title={chartData.title} data={chartData.data} />;
                        }
                      }
                    } catch {
                      // Fall through to render as code if parse fails
                    }
                  }

                  // Default code rendering
                  return <code className={className} {...props}>{children}</code>;
                },
              }}
            >
              {message.content}
            </ReactMarkdown>
            {/* Blinking cursor for streaming messages */}
            {isStreaming && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 2,
                  height: '1em',
                  backgroundColor: theme.palette.text.primary,
                  ml: 0.5,
                  verticalAlign: 'text-bottom',
                  animation: 'blink 1s step-end infinite',
                  '@keyframes blink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                  },
                }}
              />
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AiAssistantMessage;
